import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWikiOverlay } from '../../contexts/WikiOverlayContext';
import { MarkdownRenderer } from './MarkdownRenderer';
import * as drafts from '../../lib/wiki/draftsApi';

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      {status}
    </span>
  );
}

function NotConfigured() {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      Contributions need Supabase to be configured. Set REACT_APP_SUPABASE_URL and
      REACT_APP_SUPABASE_ANON_KEY, then run backend/supabase/wiki_setup.sql.
    </div>
  );
}

// ── Editor ───────────────────────────────────────────────────────────────────
export function WikiEditorView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};

  const [draftId, setDraftId] = useState(id || null);
  const [kind, setKind] = useState(prefill.kind || 'new');
  const [baseSlug, setBaseSlug] = useState(prefill.base_slug || prefill.slug || '');
  const [slug, setSlug] = useState(prefill.slug || '');
  const [lang, setLang] = useState(prefill.lang || 'en');
  const [title, setTitle] = useState(prefill.title || '');
  const [summary, setSummary] = useState(prefill.summary || '');
  const [content, setContent] = useState(prefill.content || '');
  const [status, setStatus] = useState('draft');
  const [reviewNotes, setReviewNotes] = useState('');
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!id) return undefined;
    (async () => {
      try {
        const d = await drafts.getDraft(id);
        if (mounted && d) {
          setDraftId(d.id); setKind(d.kind); setBaseSlug(d.base_slug || '');
          setSlug(d.slug); setLang(d.lang); setTitle(d.title || '');
          setSummary(d.summary || ''); setContent(d.content || '');
          setStatus(d.status); setReviewNotes(d.review_notes || '');
        }
      } catch (e) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  if (!drafts.draftsEnabled()) return <NotConfigured />;
  if (loading) return <p className="text-sm text-gray-500">Loading draft…</p>;

  const locked = status === 'approved';

  const buildFields = () => ({
    slug: kind === 'edit' ? baseSlug : slug.trim(),
    base_slug: kind === 'edit' ? baseSlug : null,
    kind, lang, title: title.trim(), summary: summary.trim(), content,
  });

  const save = async () => {
    setError(null);
    const fields = buildFields();
    if (!fields.title) { setError('Title is required'); return null; }
    if (!fields.slug) { setError('Slug is required'); return null; }
    if (!fields.content.trim()) { setError('Content is required'); return null; }
    setSaving(true);
    try {
      if (draftId) {
        await drafts.updateDraft(draftId, fields);
        return draftId;
      }
      const created = await drafts.createDraft(fields);
      setDraftId(created.id);
      navigate(`/wiki/drafts/${created.id}`, { replace: true });
      return created.id;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    const savedId = await save();
    if (!savedId) return;
    try {
      await drafts.submitDraft(savedId);
      navigate('/wiki/drafts');
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async () => {
    if (!draftId || !window.confirm('Delete this draft?')) return;
    try { await drafts.deleteDraft(draftId); navigate('/wiki/drafts'); }
    catch (e) { setError(e.message); }
  };

  const inputCls = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-gray-900">
          {draftId ? 'Edit draft' : 'New wiki draft'}
        </h1>
        <div className="flex items-center gap-2">
          <StatusBadge status={status} />
          <Link to="/wiki/drafts" className="text-sm text-blue-600 hover:underline">My drafts</Link>
        </div>
      </div>

      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {status === 'rejected' && reviewNotes && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <strong>Reviewer notes:</strong> {reviewNotes}
        </div>
      )}
      {locked && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          This draft was approved and published. Create a new draft to make further changes.
        </div>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Title</span>
            <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} disabled={locked} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Language</span>
            <select className={inputCls} value={lang} onChange={(e) => setLang(e.target.value)} disabled={locked}>
              <option value="en">English</option>
              <option value="zh">中文</option>
            </select>
          </label>
        </div>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700">
            Page path {kind === 'edit' ? '(editing existing page)' : '(e.g. product-bootcamp/my-new-page)'}
          </span>
          <input
            className={`${inputCls} font-mono ${kind === 'edit' ? 'bg-gray-50 text-gray-500' : ''}`}
            value={kind === 'edit' ? baseSlug : slug}
            onChange={(e) => setSlug(e.target.value)}
            disabled={kind === 'edit' || locked}
            placeholder="space/page-name"
          />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Summary</span>
          <input className={inputCls} value={summary} onChange={(e) => setSummary(e.target.value)} disabled={locked} />
        </label>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Content (Markdown)</span>
            <button type="button" onClick={() => setShowPreview((p) => !p)} className="text-sm text-blue-600 hover:underline">
              {showPreview ? 'Hide preview' : 'Show preview'}
            </button>
          </div>
          {showPreview ? (
            <div className="rounded-md border border-gray-200 p-4">
              <MarkdownRenderer content={content || '_Nothing to preview yet._'} />
            </div>
          ) : (
            <textarea
              className={`${inputCls} font-mono`}
              rows={18}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={locked}
              placeholder="# Heading&#10;&#10;Write your page in Markdown…"
            />
          )}
        </div>

        {!locked && (
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button type="button" onClick={save} disabled={saving}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save draft'}
            </button>
            <button type="button" onClick={submit} disabled={saving}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
              Submit for review
            </button>
            {draftId && (
              <button type="button" onClick={remove}
                className="ml-auto rounded-md px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── My drafts ────────────────────────────────────────────────────────────────
export function MyDraftsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const rows = await drafts.listMyDrafts();
        if (mounted) setItems(rows);
      } catch (e) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (!drafts.draftsEnabled()) return <NotConfigured />;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-gray-900">My drafts</h1>
        <Link to="/wiki/drafts/new" className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
          New page
        </Link>
      </div>
      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">
          You have no drafts yet. Use <strong>New page</strong> above, or open any wiki page and click
          <strong> Propose edit</strong>.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {items.map((d) => (
            <li key={d.id}>
              <Link to={`/wiki/drafts/${d.id}`} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{d.title || d.slug}</p>
                  <p className="truncate text-xs text-gray-500">
                    {d.kind === 'new' ? 'New page' : 'Edit'} · /wiki/{d.slug}
                  </p>
                </div>
                <StatusBadge status={d.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Page history ─────────────────────────────────────────────────────────────
export function HistoryView() {
  const params = useParams();
  const { language } = useLanguage();
  const slug = params['*'] || '';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const rows = await drafts.fetchHistory(slug, language);
        if (mounted) setItems(rows);
      } catch (e) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [slug, language]);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-gray-900">Edit history</h1>
        {slug && <Link to={`/wiki/${slug}`} className="text-sm text-blue-600 hover:underline">Back to page</Link>}
      </div>
      <p className="mb-6 text-sm text-gray-500 font-mono">/wiki/{slug}</p>
      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">
          No community edits recorded for this page yet. This page comes from the published wiki content.
        </p>
      ) : (
        <ol className="space-y-3">
          {items.map((rev, idx) => (
            <li key={rev.id} className="rounded-md border border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {idx === 0 ? 'Current version' : `Revision ${items.length - idx}`}
                    {rev.title ? ` — ${rev.title}` : ''}
                  </p>
                  <p className="text-xs text-gray-500">
                    by {rev.author_email || 'unknown'}
                    {rev.approved_by ? ` · approved by ${rev.approved_by}` : ''}
                    {' · '}{formatWhen(rev.created_at)}
                  </p>
                </div>
              </div>
              <details className="px-4 py-3">
                <summary className="cursor-pointer text-sm text-blue-600">View snapshot</summary>
                <div className="mt-3 border-t border-gray-100 pt-3">
                  <MarkdownRenderer content={rev.content || '_(empty)_'} />
                </div>
              </details>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ── Review queue (admin) ─────────────────────────────────────────────────────
export function ReviewView() {
  const { user, isDevMode } = useAuth();
  const { reload } = useWikiOverlay();
  const isAdmin = isDevMode || user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await drafts.listDraftsForReview();
      setItems(rows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) refresh(); else setLoading(false); }, [isAdmin, refresh]);

  if (!drafts.draftsEnabled()) return <NotConfigured />;
  if (!isAdmin) {
    return <p className="text-sm text-gray-500">The review queue is only available to administrators.</p>;
  }

  const approve = async (d) => {
    setBusyId(d.id);
    setError(null);
    try {
      await drafts.approveDraft(d, user?.email);
      await reload();
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (d) => {
    const notes = window.prompt('Reason for rejection (optional):') || '';
    setBusyId(d.id);
    setError(null);
    try {
      await drafts.rejectDraft(d.id, notes, user?.email);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-semibold text-gray-900">Review queue</h1>
      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing waiting for review. </p>
      ) : (
        <ul className="space-y-4">
          {items.map((d) => (
            <li key={d.id} className="rounded-md border border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{d.title || d.slug}</p>
                  <p className="truncate text-xs text-gray-500">
                    {d.kind === 'new' ? 'New page' : 'Edit'} · /wiki/{d.slug} · {d.lang} · by {d.author_email || 'unknown'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => reject(d)} disabled={busyId === d.id}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    Reject
                  </button>
                  <button type="button" onClick={() => approve(d)} disabled={busyId === d.id}
                    className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50">
                    {busyId === d.id ? 'Working…' : 'Approve & publish'}
                  </button>
                </div>
              </div>
              <details className="px-4 py-3">
                <summary className="cursor-pointer text-sm text-blue-600">Preview content</summary>
                <div className="mt-3 border-t border-gray-100 pt-3">
                  {d.summary && <p className="mb-3 text-sm italic text-gray-500">{d.summary}</p>}
                  <MarkdownRenderer content={d.content || '_(empty)_'} />
                </div>
              </details>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
